if ( typeof ProductQuantity !== 'function' ) {

	class ProductQuantity extends HTMLElement {

		constructor(){
			super();

			this.qtyInput = this.querySelector('.qty-selector');
			this.qtyMinus = this.querySelector('.qty-minus');
			this.qtyPlus = this.querySelector('.qty-plus');
			if (!this.qtyInput) return;

			this.qtyMin = this.qtyInput.getAttribute('min') ? parseInt(this.qtyInput.getAttribute('min'), 10) : 1;
			this._productPage = this.closest('[data-js-product-component]');
			this._cartItem = this.closest('[data-js-cart-item]');
			this._productId = this.dataset.productId || this._productPage?.dataset?.id || this._cartItem?.dataset?.productId;
			this._alertHost = this.closest('product-form') || this._productPage || this._cartItem?.closest('cart-form') || this;
			this._maxAllowed = null;
			this._inventoryMax = null;
			this._isGiftCard =
				window.__krownIsGiftCardProductPage?.(this._productPage) === true ||
				window.__krownIsGiftCardCartItem?.(this._cartItem) === true;

			this._updatePlusMinusState();
			this._bindBasicQtyControls();

			if (this._cartItem) {
				this._initCartInventoryLimit();
			} else {
				if (window.__krownGetProductQtyLimit?.(this._productId) != null) {
					this._initProductQtyLimit();
				}

				if (!this._isGiftCard && this._productPage) {
					this._initInventoryLimit();
				}
			}
		}

		_getEffectiveMax() {
			const attrMax = this.qtyInput.getAttribute('max');
			let max = attrMax ? parseInt(attrMax, 10) : 999;
			if (this._maxAllowed != null) {
				max = Math.min(max, this._maxAllowed);
			}
			if (this._inventoryMax != null) {
				max = Math.min(max, this._inventoryMax);
			}
			return Number.isFinite(max) ? max : 999;
		}

		_updatePlusMinusState() {
			const currentQty = parseInt(this.qtyInput.value, 10) || this.qtyMin;
			const qtyMax = this._getEffectiveMax();

			if (currentQty - 1 < this.qtyMin) {
				this.qtyMinus?.classList.add('disabled');
			} else {
				this.qtyMinus?.classList.remove('disabled');
			}

			if (currentQty + 1 > qtyMax) {
				this.qtyPlus?.classList.add('disabled');
			} else {
				this.qtyPlus?.classList.remove('disabled');
			}
		}

		_getLimitAlertMessage() {
			if (this._cartItem) {
				const form = this._cartItem.closest('cart-form')?.querySelector('form');
				return window.__krownGetCartLineLimitMessage?.(form, this._cartItem) || '';
			}
			// Gift cards: hard product LIMITS only — never availability_limit / stock copy
			if (this._isGiftCard) {
				return (
					window.__krownGetProductQtyLimitMessage?.(this._productId) ||
					'You can only add up to 20 of this item to your cart.'
				);
			}
			const ctx = window.__krownGetProductPageCapContext?.(
				this._productPage,
				this._productId,
				this.qtyInput
			);
			if (ctx && (ctx.stockLimit != null || ctx.ruleMax != null)) {
				return (
					window.__krownResolveStockMessage?.('cart_at_cap', {
						ruleMax: ctx.ruleMax,
						stockLimit: ctx.stockLimit,
					}) || ''
				);
			}
			return window.__krownGetProductQtyLimitMessage?.(this._productId) || '';
		}

		_showLimitAlert() {
			if (this._cartItem) {
				// CartProductQuantity owns cart + clicks — avoid duplicate banners
				const form = this._cartItem.closest('cart-form')?.querySelector('form');
				window.__krownShowCartLineLimitAlert?.(form, this._cartItem);
				return;
			}
			const ctx = window.__krownGetProductPageCapContext?.(
				this._productPage,
				this._productId,
				this.qtyInput
			);
			const message = this._getLimitAlertMessage();
			window.__krownShowProductQtyLimitAlert?.(this._alertHost, this._productId, null, {
				message: message || undefined,
				scenario: message ? undefined : 'cart_at_cap',
				ruleMax: ctx?.ruleMax ?? window.__krownGetProductQtyLimit?.(this._productId),
				stockLimit: ctx?.stockLimit ?? window.__krownGetAvailabilityStockLimit?.(this._productPage),
			});
		}

		_enforceLimit(showAlert) {
			const qtyMax = this._getEffectiveMax();
			let currentQty = parseInt(this.qtyInput.value, 10);

			if (!Number.isFinite(currentQty) || currentQty < this.qtyMin) {
				this.qtyInput.value = Math.min(this.qtyMin, Math.max(0, qtyMax));
				currentQty = parseInt(this.qtyInput.value, 10);
			}

			if (currentQty > qtyMax) {
				this.qtyInput.value = qtyMax;
				if (showAlert) {
					this._showLimitAlert();
				}
			}

			this._updatePlusMinusState();
		}

		_bindBasicQtyControls() {
			this.qtyInput.addEventListener('input', () => {
				this._enforceLimit(true);
			});

			this.qtyInput.addEventListener('change', () => {
				this._enforceLimit(true);
			});

			// Nested cart-product-quantity owns cart sync + at-cap alerts, but this
			// element still mutates the input so minus works while at max.
			const nestedCartQty =
				!!(this._cartItem && this.querySelector('cart-product-quantity'));

			this.qtyPlus?.addEventListener('click', (e) => {
				e.preventDefault();
				if (this.qtyPlus.classList.contains('disabled')) {
					// Cart: CartProductQuantity shows the under-line message
					if (!nestedCartQty) {
						this._showLimitAlert();
					}
					return;
				}
				const currentQty = parseInt(this.qtyInput.value, 10) || this.qtyMin;
				const qtyMax = this._getEffectiveMax();
				if (currentQty + 1 <= qtyMax) {
					this.qtyInput.value = currentQty + 1;
				} else if (!nestedCartQty) {
					this._showLimitAlert();
				}
				this._updatePlusMinusState();
			});

			this.qtyMinus?.addEventListener('click', (e) => {
				e.preventDefault();
				if (this.qtyMinus.classList.contains('disabled')) return;
				const currentQty = parseInt(this.qtyInput.value, 10) || this.qtyMin;
				if (currentQty - 1 >= this.qtyMin) {
					this.qtyInput.value = currentQty - 1;
				}
				this._updatePlusMinusState();
			});
		}

		_applyInventoryMax(maxForLine) {
			this._inventoryMax = maxForLine;
			if (maxForLine != null && maxForLine > 0) {
				this.qtyInput.setAttribute('max', String(maxForLine));
			} else if (maxForLine === 0) {
				this.qtyInput.setAttribute('max', '0');
			}
			this._enforceLimit(false);
		}

		_refreshCartInventoryLimit() {
			if (!this._cartItem) return;

			const form = this._cartItem.closest('cart-form')?.querySelector('form');
			const maxForLine = window.__krownGetMaxQtyForCartLine?.(form, this._cartItem);
			if (maxForLine == null) {
				this._inventoryMax = null;
				this._enforceLimit(false);
				return;
			}
			this._applyInventoryMax(maxForLine);
		}

		_initCartInventoryLimit() {
			this._refreshCartInventoryLimit();

			this._onCartInventoryChanged = () => {
				this._refreshCartInventoryLimit();
			};
			document.addEventListener('add-to-cart', this._onCartInventoryChanged);
			document.addEventListener('cart-updated', this._onCartInventoryChanged);
		}

		_refreshInventoryLimit() {
			if (this._isGiftCard) return;

			const stockLimit = window.__krownGetAvailabilityStockLimit?.(this._productPage);
			if (stockLimit == null) {
				// Do NOT strip Liquid max — leave existing max attribute intact.
				this._inventoryMax = null;
				this._enforceLimit(false);
				return;
			}

			const variantInput =
				this.closest('product-form')?.querySelector('[name="id"]') ||
				this._productPage?.querySelector('[name="id"]');
			const variantId = variantInput?.value;

			const applyLimit = (cartVariantQty) => {
				this._applyInventoryMax(Math.max(0, stockLimit - (cartVariantQty || 0)));
			};

			if (variantId && typeof window.__krownFetchCartQtyForVariant === 'function') {
				window.__krownFetchCartQtyForVariant(variantId).then(applyLimit);
			} else {
				applyLimit(0);
			}
		}

		_initInventoryLimit() {
			this._refreshInventoryLimit();

			const variants = this._productPage?.querySelector('product-variants');
			this._onVariantChange = () => {
				this._refreshInventoryLimit();
			};
			variants?.addEventListener('VARIANT_CHANGE', this._onVariantChange);

			this._onInventoryCartChanged = () => {
				this._refreshInventoryLimit();
			};
			document.addEventListener('add-to-cart', this._onInventoryCartChanged);
			document.addEventListener('cart-updated', this._onInventoryCartChanged);
		}

		async _refreshLimitFromCart() {
			if (!this._productId) return;

			const limit = window.__krownGetProductQtyLimit(this._productId);
			if (limit == null) return;

			const cartQty = await window.__krownFetchCartQtyForProduct(this._productId);
			this._maxAllowed = Math.max(0, limit - cartQty);

			if (this._maxAllowed <= 0) {
				this.qtyInput.setAttribute('max', '0');
				this.qtyPlus?.classList.add('disabled');
				this._showLimitAlert();
				this._updatePlusMinusState();
				return;
			}

			this.qtyInput.setAttribute('max', String(this._maxAllowed));
			this._enforceLimit(false);
		}

		_initProductQtyLimit() {
			this._refreshLimitFromCart();

			this._onCartChanged = () => {
				this._refreshLimitFromCart();
			};

			document.addEventListener('add-to-cart', this._onCartChanged);
			document.addEventListener('cart-updated', this._onCartChanged);
		}

		disconnectedCallback() {
			if (this._onCartChanged) {
				document.removeEventListener('add-to-cart', this._onCartChanged);
				document.removeEventListener('cart-updated', this._onCartChanged);
			}
			if (this._onInventoryCartChanged) {
				document.removeEventListener('add-to-cart', this._onInventoryCartChanged);
				document.removeEventListener('cart-updated', this._onInventoryCartChanged);
			}
			if (this._onCartInventoryChanged) {
				document.removeEventListener('add-to-cart', this._onCartInventoryChanged);
				document.removeEventListener('cart-updated', this._onCartInventoryChanged);
			}
			const variants = this._productPage?.querySelector('product-variants');
			if (this._onVariantChange && variants) {
				variants.removeEventListener('VARIANT_CHANGE', this._onVariantChange);
			}
		}
	}


  if ( typeof customElements.get('product-quantity') == 'undefined' ) {
    customElements.define('product-quantity', ProductQuantity);
	}

}
